import React, { useCallback, useEffect, useState } from 'react';
import { X, Image as ImageIcon, Video as VideoIcon, Search, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { api, fileUrl } from '../../lib/api';

const MediaPicker = ({ open, kind = 'any', onPick, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/media');
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const filtered = items
    .filter((it) => kind === 'any' || it.kind === kind)
    .filter((it) => !q || it.original_filename.toLowerCase().includes(q.toLowerCase()));

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[85vh] rounded-2xl border border-white/10 bg-[#0a0d17] flex flex-col"
      >
        <div className="p-5 flex items-center justify-between border-b border-white/8">
          <div>
            <div className="text-white font-semibold">Pick from library</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {kind === 'image' ? 'Images only' : kind === 'video' ? 'Videos only' : 'All media'} · upload new items from the Media tab
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 text-white btn-transition flex items-center justify-center" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 border-b border-white/8">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by filename…"
              className="pl-9 bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
            />
          </div>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">No media found.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  onClick={() => onPick(fileUrl(it.url))}
                  className="group text-left rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden hover:border-brand-green/50 btn-transition"
                >
                  <div className="aspect-video bg-black/40 overflow-hidden">
                    {it.kind === 'image' ? (
                      <img src={fileUrl(it.url)} alt={it.original_filename} className="w-full h-full object-cover group-hover:scale-105 btn-transition" style={{ transitionDuration: '400ms' }} loading="lazy" />
                    ) : (
                      <video src={fileUrl(it.url)} className="w-full h-full object-cover" preload="metadata" muted />
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      {it.kind === 'image' ? <ImageIcon className="h-3 w-3" /> : <VideoIcon className="h-3 w-3" />} {it.kind}
                    </div>
                    <div className="text-xs text-white truncate mt-0.5">{it.original_filename}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
